import React, { useEffect } from 'react';

import Divider from '../../components/shared/Divider';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

type DrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    width?: string;
    showOverlay?: boolean;
    closeOnEsc?: boolean;
    title?: string;
    showCloseButton?: boolean;
    headerSlot?: React.ReactNode;
};

const Drawer: React.FC<DrawerProps> = ({
    isOpen,
    onClose,
    children,
    width = '400px',
    showOverlay = true,
    closeOnEsc = true,
    title,
    showCloseButton,
    headerSlot,
}) => {
    useEffect(() => {
        if (!isOpen || !closeOnEsc) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeOnEsc, onClose]);

    if (!isOpen) return null;

    return (
        <>
            {showOverlay && <DrawerOverlay onClick={onClose} />}
            <DrawerContainer width={width} data-testid="drawer">
                {(title || showCloseButton || headerSlot) && (
                    <DrawerHeader>
                        {title && <DrawerLabel>{title}</DrawerLabel>}
                        <HeaderActions>
                            {headerSlot}
                            {showCloseButton && (
                                <CloseButton
                                    onClick={onClose}
                                    aria-label="Close Drawer"
                                >
                                    ×
                                </CloseButton>
                            )}
                        </HeaderActions>
                    </DrawerHeader>
                )}
                <Divider />
                {children}
            </DrawerContainer>
        </>
    );
};

export default Drawer;

const DrawerOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 999;
`;

const DrawerContainer = styled.div<{ width: string }>`
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: ${({ width }) => width};
    background-color: white;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    z-index: 1000;
`;
const DrawerHeader = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: aampeTheme.spacing4(5, 5, 4, 5),
});
const HeaderActions = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
});

const DrawerLabel = styled.h2({
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.light,
    fontFamily: 'Inclusive Sans Variable, Arial, sans-serif',
    lineHeight: 1,
    color: aampeTheme.colors.neutral09,
});
const CloseButton = styled.button({
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
});
